/**
 * Books payout bills — the READ side of "approvals become Books bills"
 * (migration 20261008_books_payout_bills, Books 0053; Oct 8, 2026).
 *
 * Since that migration an approval on the Commission board (RM / TL / ASM
 * commissions, the RS pool, roof crew payments, canvasser pay, TL monthly
 * bonuses) and "Send to Books" on the SPIFF tracker no longer end with Shannon
 * marking the row paid here: the approve RPC stores the lines and makes ONE
 * bill per person per approval batch in `books_payout_bills`, a trigger sends
 * it to Books, Shannon pays it there from the bank feed, and Books reports
 * back. The CRM's pull (`books_payout_pull`, cron every 5 minutes or "Check
 * Books now") then writes the commission_payments rows / flips the paid flags.
 *
 * This module is the one seam the screens read bills through:
 *   • `fetchBooksPayoutBills()` — the bills + lines for a set of source rows
 *     (deals, roof_crew_payments, …), for one member, or by bill id. RLS: the
 *     finance tiers read everything, a person reads their own bills.
 *   • `indexBills()` / `liveBillsFor()` — which LIVE (not voided) bill carries a
 *     given source row (+ member, + line type).
 *   • `billChipText()` — the one-line chip every screen shows for a bill. Pure;
 *     unit-tested in booksPayoutBills.test.ts.
 *
 * Nothing here writes: the bills are written only by the approve / un-approve
 * RPCs and the pull.
 */
import { supabase } from "./supabase";

// "pm" (Oct 21, 2026): the project manager's fee, one bill to the crew's vendor every other Friday (pm_books_bill_make)
export type BooksPayoutBillKind = "commission" | "rs_pool" | "crew" | "canvasser" | "tl_bonus" | "spiff" | "pm";
export type BooksPayoutBillStatus = "held" | "sent" | "part_paid" | "paid" | "voided";

export interface BooksPayoutBill {
  id: string;
  kind: BooksPayoutBillKind;
  batch_id: string;
  payee_kind: "member" | "vendor";
  member_id: string | null;
  vendor_id: string | null;
  /** Dollars (numeric(12,2) in the DB; PostgREST returns it as a string — normalized here). */
  total: number;
  approved_at: string;
  status: BooksPayoutBillStatus;
  hold_reason: string | null;
  books_sent_at: string | null;
  books_confirmed_at: string | null;
  books_last_error: string | null;
  books_invoice_no: string | null;
  /** YYYY-MM-DD, set by Books when it paid (any part of) the bill. */
  paid_on: string | null;
  paid_cash: number;
  paid_draws: number;
  paid_method: string | null;
  paid_reference: string | null;
  voided_at: string | null;
  void_reason: string | null;
}

export interface BooksPayoutBillLine {
  id: string;
  bill_id: string;
  source_table: string;
  source_id: string;
  deal_id: string | null;
  line_type: string;
  amount: number;
  memo: string | null;
}

export const BOOKS_PAYOUT_BILL_SELECT =
  "id,kind,batch_id,payee_kind,member_id,vendor_id,total,approved_at,status,hold_reason,books_sent_at,books_confirmed_at,books_last_error,books_invoice_no,paid_on,paid_cash,paid_draws,paid_method,paid_reference,voided_at,void_reason";
export const BOOKS_PAYOUT_BILL_LINE_SELECT = "id,bill_id,source_table,source_id,deal_id,line_type,amount,memo";

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// PostgREST returns numerics as STRINGS — normalize every money field on the way
// in so no reader ever does arithmetic on a string.
export function normalizeBill(raw: Record<string, unknown>): BooksPayoutBill {
  return {
    id: String(raw.id),
    kind: raw.kind as BooksPayoutBillKind,
    batch_id: String(raw.batch_id),
    payee_kind: raw.payee_kind as "member" | "vendor",
    member_id: (raw.member_id as string | null) ?? null,
    vendor_id: (raw.vendor_id as string | null) ?? null,
    total: num(raw.total),
    approved_at: String(raw.approved_at),
    status: raw.status as BooksPayoutBillStatus,
    hold_reason: (raw.hold_reason as string | null) ?? null,
    books_sent_at: (raw.books_sent_at as string | null) ?? null,
    books_confirmed_at: (raw.books_confirmed_at as string | null) ?? null,
    books_last_error: (raw.books_last_error as string | null) ?? null,
    books_invoice_no: (raw.books_invoice_no as string | null) ?? null,
    paid_on: (raw.paid_on as string | null) ?? null,
    paid_cash: num(raw.paid_cash),
    paid_draws: num(raw.paid_draws),
    paid_method: (raw.paid_method as string | null) ?? null,
    paid_reference: (raw.paid_reference as string | null) ?? null,
    voided_at: (raw.voided_at as string | null) ?? null,
    void_reason: (raw.void_reason as string | null) ?? null,
  };
}

export function normalizeLine(raw: Record<string, unknown>): BooksPayoutBillLine {
  return {
    id: String(raw.id),
    bill_id: String(raw.bill_id),
    source_table: String(raw.source_table),
    source_id: String(raw.source_id),
    deal_id: (raw.deal_id as string | null) ?? null,
    line_type: String(raw.line_type),
    amount: num(raw.amount),
    memo: (raw.memo as string | null) ?? null,
  };
}

/** A bill still standing in Books: anything not withdrawn (held / sent / part-paid / paid). */
export function isLiveBill(b: BooksPayoutBill): boolean {
  return b.status !== "voided";
}

/** A bill Shannon still has to pay in Books (nothing paid on it yet). */
export function isUnpaidBill(b: BooksPayoutBill): boolean {
  return b.status === "held" || b.status === "sent";
}

export interface BillIndex {
  /** Every bill fetched, by id. */
  bills: Map<string, BooksPayoutBill>;
  /** `${source_table}:${source_id}` → the lines on that source row (all bills). */
  linesBySource: Map<string, BooksPayoutBillLine[]>;
}

export const EMPTY_BILL_INDEX: BillIndex = { bills: new Map(), linesBySource: new Map() };

export function sourceKey(sourceTable: string, sourceId: string): string {
  return `${sourceTable}:${sourceId}`;
}

export function indexBills(bills: BooksPayoutBill[], lines: BooksPayoutBillLine[]): BillIndex {
  const byId = new Map<string, BooksPayoutBill>();
  for (const b of bills) byId.set(b.id, b);
  const linesBySource = new Map<string, BooksPayoutBillLine[]>();
  for (const l of lines) {
    const k = sourceKey(l.source_table, l.source_id);
    const arr = linesBySource.get(k);
    if (arr) arr.push(l);
    else linesBySource.set(k, [l]);
  }
  return { bills: byId, linesBySource };
}

/**
 * The LIVE bills that carry a source row — optionally only one member's, only
 * one line type's, only one kind's. Newest approval first. The same bill is
 * returned once even when several of its lines are on the row (an RM + split).
 */
export function liveBillsFor(
  index: BillIndex,
  sourceTable: string,
  sourceId: string,
  opts: { memberId?: string | null; lineType?: string | null; kind?: BooksPayoutBillKind | null } = {},
): BooksPayoutBill[] {
  const lines = index.linesBySource.get(sourceKey(sourceTable, sourceId)) ?? [];
  const seen = new Set<string>();
  const out: BooksPayoutBill[] = [];
  for (const l of lines) {
    if (opts.lineType && l.line_type !== opts.lineType) continue;
    const b = index.bills.get(l.bill_id);
    if (!b || !isLiveBill(b)) continue;
    if (opts.memberId && b.member_id !== opts.memberId) continue;
    if (opts.kind && b.kind !== opts.kind) continue;
    if (seen.has(b.id)) continue;
    seen.add(b.id);
    out.push(b);
  }
  return out.sort((a, b) => (a.approved_at < b.approved_at ? 1 : a.approved_at > b.approved_at ? -1 : 0));
}

/** The newest live bill on a source row, or null (mirrors `books_payout_live_bill()` in SQL). */
export function liveBillFor(
  index: BillIndex,
  sourceTable: string,
  sourceId: string,
  opts: { memberId?: string | null; lineType?: string | null; kind?: BooksPayoutBillKind | null } = {},
): BooksPayoutBill | null {
  return liveBillsFor(index, sourceTable, sourceId, opts)[0] ?? null;
}

// ---------------------------------------------------------------------------
// The chip
// ---------------------------------------------------------------------------

export type BillChipTone = "sent" | "held" | "paid" | "part_paid" | "refused" | "voided";

function fmtMoney(n: number): string {
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A timestamptz → "Oct 8" (Eastern, the company's calendar). */
function fmtStamp(iso: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" });
}

/** A date-only (YYYY-MM-DD) → "Oct 8" without the UTC-midnight off-by-one trap. */
function fmtDay(ymd: string | null): string | null {
  if (!ymd) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return fmtStamp(ymd);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12)).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
}

/** Books' payment method ('ach', 'check', 'zelle', …) as a person reads it. */
export function paidMethodLabel(method: string | null): string {
  if (!method) return "";
  const m = method.replace(/^books:/, "").trim();
  const known: Record<string, string> = { ach: "ACH", check: "check", wire: "wire", zelle: "Zelle", cash: "cash", card: "card", draws: "draws" };
  return known[m.toLowerCase()] ?? m;
}

/**
 * Which colour a bill's chip takes. A refusal (Books answered an error to the
 * bill) wins over everything but a payment: a bill Books refused is not in
 * Books, whatever its status column says, until the tick re-sends it.
 */
export function billChipTone(b: BooksPayoutBill): BillChipTone {
  if (b.status === "voided") return "voided";
  if (b.status === "paid") return "paid";
  if (b.status === "part_paid") return "part_paid";
  if (b.books_last_error) return "refused";
  if (b.status === "held") return "held";
  return "sent";
}

/**
 * One line per bill, as the screens show it:
 *   "Bill in Books — $1,234.00 · sent Oct 8"
 *   "Bill in Books — $1,234.00 · held: credits ≥ $501 until the balance is collected"
 *   "Paid in Books on Oct 9 (ACH)"            (+ " · $200.00 of it from draws" when draws applied)
 *   "Paid in part in Books — $500.00 of $1,234.00 (Oct 9, ACH)"
 *   "Books refused: unknown event_type"
 *   "Bill withdrawn Oct 8 — <reason>"
 */
export function billChipText(b: BooksPayoutBill): string {
  const total = fmtMoney(b.total);
  switch (billChipTone(b)) {
    case "voided": {
      const when = fmtStamp(b.voided_at);
      return `Bill withdrawn${when ? ` ${when}` : ""}${b.void_reason ? ` — ${b.void_reason}` : ""}`;
    }
    case "paid": {
      const when = fmtDay(b.paid_on);
      const method = paidMethodLabel(b.paid_method);
      const draws = b.paid_draws > 0 ? ` · ${fmtMoney(b.paid_draws)} of it from draws` : "";
      return `Paid in Books${when ? ` on ${when}` : ""}${method ? ` (${method})` : ""}${draws}`;
    }
    case "part_paid": {
      const when = fmtDay(b.paid_on);
      const method = paidMethodLabel(b.paid_method);
      const paid = fmtMoney(b.paid_cash + b.paid_draws);
      const tail = [when, method].filter(Boolean).join(", ");
      return `Paid in part in Books — ${paid} of ${total}${tail ? ` (${tail})` : ""}`;
    }
    case "refused":
      return `Books refused: ${b.books_last_error}`;
    case "held":
      return `Bill in Books — ${total} · held: ${b.hold_reason ?? "pay hold"}`;
    case "sent":
    default: {
      const when = fmtStamp(b.books_sent_at ?? b.approved_at);
      const confirmed = b.books_confirmed_at ? "" : b.books_sent_at ? " · awaiting Books' answer" : " · sending…";
      return `Bill in Books — ${total} · sent${when ? ` ${when}` : ""}${confirmed}`;
    }
  }
}

/** Tailwind classes for the chip, by tone — one look across every screen. */
export const BILL_CHIP_CLASS: Record<BillChipTone, string> = {
  sent: "bg-sky-50 text-sky-700 border border-sky-200",
  held: "bg-amber-50 text-amber-800 border border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  part_paid: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  refused: "bg-red-50 text-red-700 border border-red-200",
  voided: "bg-slate-100 text-slate-500 border border-slate-200",
};

// ---------------------------------------------------------------------------
// Loader
// ---------------------------------------------------------------------------

export interface FetchBillsOpts {
  /** Bills with a line on these source rows (one lines query, then one bills query). */
  sourceTable?: string;
  sourceIds?: string[];
  /** Bills by id (e.g. the `books_bill_id` column of a payout row). */
  billIds?: string[];
  /** One person's bills (what a rep sees on My Commissions). */
  memberId?: string;
  kinds?: BooksPayoutBillKind[];
  /** Default true: withdrawn bills are left out. */
  liveOnly?: boolean;
}

/**
 * The bills (+ their lines) matching the options. Every filter given narrows
 * the set; with none, nothing is fetched (never "everything"). Throws on a read
 * error — a money screen must not silently show "no bill" when the read failed.
 */
export async function fetchBooksPayoutBills(opts: FetchBillsOpts): Promise<BillIndex> {
  const liveOnly = opts.liveOnly ?? true;
  const sourceIds = [...new Set(opts.sourceIds ?? [])];
  let billIds = [...new Set(opts.billIds ?? [])];
  const bySource = !!opts.sourceTable && sourceIds.length > 0;
  const byMember = !!opts.memberId;
  if (!bySource && !byMember && billIds.length === 0) return EMPTY_BILL_INDEX;

  let sourceLines: BooksPayoutBillLine[] = [];
  if (bySource) {
    const { data, error } = await supabase
      .from("books_payout_bill_lines")
      .select(BOOKS_PAYOUT_BILL_LINE_SELECT)
      .eq("source_table", opts.sourceTable!)
      .in("source_id", sourceIds);
    if (error) throw error;
    sourceLines = (data ?? []).map((r) => normalizeLine(r as Record<string, unknown>));
    billIds = [...new Set([...billIds, ...sourceLines.map((l) => l.bill_id)])];
    if (!byMember && billIds.length === 0) return EMPTY_BILL_INDEX;
  }

  // One person's bills are read whole (the chip on My Commissions needs the
  // newest per deal); otherwise only the bills the lines / ids named.
  let q = supabase.from("books_payout_bills").select(BOOKS_PAYOUT_BILL_SELECT).order("approved_at", { ascending: false });
  if (byMember) q = q.eq("member_id", opts.memberId!);
  else q = q.in("id", billIds);
  if (opts.kinds && opts.kinds.length > 0) q = q.in("kind", opts.kinds);
  if (liveOnly) q = q.neq("status", "voided");
  const { data: billRows, error: billErr } = await q;
  if (billErr) throw billErr;
  const bills = (billRows ?? []).map((r) => normalizeBill(r as Record<string, unknown>));
  if (bills.length === 0) return EMPTY_BILL_INDEX;

  // Lines: what the source query brought, plus every line of a bill reached by
  // id or by member (so a bill's other rows are known too).
  const haveLinesFor = new Set(sourceLines.map((l) => l.bill_id));
  const need = bills.map((b) => b.id).filter((id) => !haveLinesFor.has(id) || byMember || (opts.billIds?.length ?? 0) > 0);
  let moreLines: BooksPayoutBillLine[] = [];
  if (need.length > 0) {
    const { data, error } = await supabase
      .from("books_payout_bill_lines")
      .select(BOOKS_PAYOUT_BILL_LINE_SELECT)
      .in("bill_id", need);
    if (error) throw error;
    moreLines = (data ?? []).map((r) => normalizeLine(r as Record<string, unknown>));
  }
  const lineById = new Map<string, BooksPayoutBillLine>();
  for (const l of [...sourceLines, ...moreLines]) lineById.set(l.id, l);
  const billIdSet = new Set(bills.map((b) => b.id));
  return indexBills(bills, [...lineById.values()].filter((l) => billIdSet.has(l.bill_id)));
}

/** The one-line summary "Check Books now" shows after `books_payout_pull()`. */
export function pullResultText(res: unknown): string {
  const r = (res ?? {}) as { released?: number; resent?: number; confirmed?: number; applied?: number; errors?: unknown[] };
  const n = (v: unknown) => (typeof v === "number" ? v : Number(v) || 0);
  const errs = Array.isArray(r.errors) ? r.errors.length : 0;
  const parts = [
    `${n(r.released)} released from hold`,
    `${n(r.resent)} re-sent`,
    `${n(r.confirmed)} confirmed by Books`,
    `${n(r.applied)} payment${n(r.applied) === 1 ? "" : "s"} applied`,
  ];
  return `Checked Books: ${parts.join(" · ")}${errs > 0 ? ` · ${errs} error${errs === 1 ? "" : "s"} (see the Finance audit log)` : ""}.`;
}
